import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Headphones } from 'lucide-react';
import { generateTTS, getVoices, getModels } from '../../services/speechApi';
import { toast } from '../ui/Toast';
import SpeechAudioPlayer from './SpeechAudioPlayer';

const CHAR_LIMITS = { openai: 4096, elevenlabs: 5000 };

const SpeechTTS = () => {
  const [provider, setProvider] = useState('openai');
  const [models, setModels] = useState({});
  const [model, setModel] = useState('tts-1');
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState('alloy');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Charger le catalogue modèles au montage
  useEffect(() => {
    getModels().then(data => setModels(data.tts || {})).catch(() => {});
  }, []);

  // Charger les voix quand le provider change
  useEffect(() => {
    setLoadingVoices(true);
    getVoices(provider)
      .then(v => {
        setVoices(v);
        if (v.length > 0) setVoice(v[0].id);
      })
      .catch(() => setVoices([]))
      .finally(() => setLoadingVoices(false));

    // Set modèle par défaut selon provider
    if (provider === 'openai') setModel('tts-1');
    else if (provider === 'elevenlabs') setModel('eleven_flash_v2_5');
  }, [provider]);

  const charLimit = CHAR_LIMITS[provider] || 4096;

  const handleGenerate = async () => {
    if (!text.trim()) return toast.warning('Entrez un texte à synthétiser');
    if (text.length > charLimit) return toast.error(`Texte trop long (max ${charLimit} caractères)`);

    setLoading(true);
    // Libérer l'ancien blob
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);

    try {
      const url = await generateTTS(text, provider, voice, model);
      setAudioUrl(url);
      toast.success('Audio généré');
    } catch (err) {
      toast.error(err.message || 'Erreur génération audio');
    } finally {
      setLoading(false);
    }
  };

  const providerModels = models[provider] || [];

  return (
    <div style={{ display: 'flex', gap: '24px', height: '100%', padding: '24px 0' }}>

      {/* Colonne gauche — Configuration */}
      <div style={{ flex: '0 0 380px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Provider */}
        <Field label="Provider">
          <select value={provider} onChange={e => setProvider(e.target.value)} style={selectStyle}>
            <option value="openai">OpenAI</option>
            <option value="elevenlabs">ElevenLabs</option>
          </select>
        </Field>

        {/* Modèle */}
        <Field label="Modèle">
          <select value={model} onChange={e => setModel(e.target.value)} style={selectStyle}>
            {providerModels.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          {providerModels.find(m => m.id === model)?.description && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {providerModels.find(m => m.id === model).description}
            </div>
          )}
        </Field>

        {/* Voix */}
        <Field label="Voix">
          {loadingVoices ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Chargement...
            </div>
          ) : (
            <select value={voice} onChange={e => setVoice(e.target.value)} style={selectStyle}>
              {voices.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.labels?.gender ? ` (${v.labels.gender})` : ''}
                </option>
              ))}
            </select>
          )}
        </Field>

        {/* Textarea script */}
        <Field label="Script">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Entrez le texte à convertir en audio..."
            maxLength={charLimit}
            rows={8}
            style={{
              ...inputBaseStyle,
              resize: 'vertical', minHeight: '140px', maxHeight: '300px',
              fontFamily: 'inherit', lineHeight: 1.6,
            }}
          />
          <div style={{
            display: 'flex', justifyContent: 'flex-end', fontSize: '11px', marginTop: '4px',
            color: text.length > charLimit * 0.9 ? '#ef4444' : 'var(--text-secondary)',
          }}>
            {text.length} / {charLimit}
          </div>
        </Field>

        {/* Bouton Générer */}
        <button
          onClick={handleGenerate}
          disabled={loading || !text.trim()}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            padding: '14px 24px', borderRadius: '12px', border: 'none', cursor: loading ? 'wait' : 'pointer',
            background: loading || !text.trim()
              ? 'var(--card-border)'
              : 'linear-gradient(135deg, #1D4ED8 0%, #2563eb 100%)',
            color: '#fff', fontWeight: 600, fontSize: '15px',
            boxShadow: loading || !text.trim() ? 'none' : '0 4px 18px rgba(29,78,216,0.4)',
            transition: 'all 0.2s', width: '100%',
          }}
        >
          {loading ? (
            <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Génération en cours...</>
          ) : (
            <><Sparkles size={18} /> Générer</>
          )}
        </button>
      </div>

      {/* Colonne droite — Résultat */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {audioUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Headphones size={16} style={{ color: '#3b82f6' }} />
              Audio généré
            </div>
            <SpeechAudioPlayer blobUrl={audioUrl} />
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {provider === 'openai' ? 'OpenAI' : 'ElevenLabs'} — {providerModels.find(m => m.id === model)?.name || model} — {voices.find(v => v.id === voice)?.name || voice}
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', opacity: 0.4, gap: '12px',
          }}>
            <Headphones size={48} style={{ color: 'var(--text-secondary)' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              {loading ? 'Génération en cours...' : 'Entrez un texte et cliquez sur Générer'}
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Composants utilitaires ────────────────────────────────────

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
      {label}
    </label>
    {children}
  </div>
);

const inputBaseStyle = {
  width: '100%', padding: '10px 14px', borderRadius: '10px',
  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
  color: 'var(--text)', fontSize: '14px', outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.15s',
};

const selectStyle = {
  ...inputBaseStyle,
  cursor: 'pointer', appearance: 'auto',
};

export default SpeechTTS;
